import useAxios from "../../../../utils/useAxios";
import AuthContext from "../../../../context/AuthContext";
import { useContext, useMemo } from "react";
import { ExpandableSection } from "routes/skus/components/expandable-section";
import AsyncSelect from "react-select/async";
import useArtworkCollection from "../../hooks/useArtworkCollectionHook";
import {
  ArtworkCollectionPersistentData,
  ArtworkMetaPersistentData,
  ArtworkPrintingPersistentData,
} from "../../../../types/persistent-page-filters";
import {
  DebounceContext,
  DebounceContextType,
} from "../../../../context/DebounceContext";
import {
  buildArtworkCollectionSearchFilterObject,
  buildArtworkPrintingTechniqueSearchFilterObject,
  buildMetaArtworkSearchFilterObject,
  getPrintingTechniques,
} from "../../../../helpers/artwork-searches.helpers";
import { storeLastFiltersAndResults } from "../../../../helpers";
import { Predicates } from "../../../../libraries/predicates/predicates";
import {
  formatCollectionOptions,
  getCollections,
} from "../../helpers/collections.helper";
import OptionFormatter from "../../../../components/OptionFormatter";
import {
  formatLicensedPropertyIntoOption,
  getLicensedPropertys,
} from "../../../../helpers/licensed-property.helper";
import useLicensedProperty, {
  fetchLicensedProperty,
} from "../../../../hooks/useLicensedProperty";
import { useLicensorContext } from "../../../../context/LicensorContext";
import {
  formatLicensorIntoOption,
  getLicensors,
} from "../../../../helpers/licensor.helper";
import { useMasterArtworkCollectionStore } from "../../../../context/MasterArtworkCollectionContext";
import { MasterArtworkCollectionActions } from "../../../../states/meta-artwork/collections";
import Select from "react-select";
import {
  getGlobalProduct,
  getGlobalProducts,
  globalProductOptions,
} from "../../../../helpers/global-product.helper";
import useGlobalProduct from "../../../../hooks/useGlobalProduct";
import { useMasterArtworkPrintingTechniqueStore } from "../../../../context/MasterArtworkPrintingTechniqueContext";
import { useMoldContext } from "../../../../context/MoldContext";
import { getMolds } from "../../../../helpers/mold.helper";
import { fetchMold } from "../../../../hooks/useMold";
import useArtworkName from "../../hooks/useArtworkNameHook";
import useArtworkNumber from "../../hooks/useArtworkNumberHook";
import { useDecorationTechContext } from "../../../../context/DecorationTechContext";
import { useColorContext } from "../../../../context/ColorContext";
import {
  formatArtworkNumberOptions,
  getArtworkNumber,
} from "../../helpers/artworkNumber.helper";
import {
  formatArtworkNameOptions,
  getArtworkName,
} from "../../helpers/artworkName.helper";
import { fetchColor } from "../../../../hooks/useColor";
import {
  formatColorIntoOption,
  getColors,
} from "../../../../helpers/color.helper";
import { MasterArtworkPrintingTechniqueActions } from "../../../../states/meta-artwork/printing-technique";
import {
  formatDecorTechIntoOption,
  getDecorTechs,
} from "../../../../helpers/decor-tech.helper";
import { isStateEqualToInitialState as isCollectionStateInitial } from "../../../../states/meta-artwork/collections/Reducer";
import { isStateEqualToInitialState as isPrintingTechniqueStateInitial } from "../../../../states/meta-artwork/printing-technique/Reducer";
import { useMasterArtworkNumberStore } from "../../../../context/MasterArtworkNumberContext";
import { MasterArtworkNumberActions } from "../../../../states/meta-artwork/artwork-number";
import { isStateEqualToInitialState as isArtworkNumberStateInitial } from "../../../../states/meta-artwork/artwork-number/Reducer";
import useArtworkPrintingTechnique from "../../hooks/useArtworkPrintingTechniqueHook";
import { useMetaArtworkSearchStore } from "../../../../context/MetaArtworkSearchContext";
import { MasterArtworkSearchActions } from "../../../../states/meta-artwork/search";

type ArtworkLevelType = {
  collection: boolean;
  metaArtwork: boolean;
  printingTechnique: boolean;
};

const SearchFilters = ({
  filterProducts,
  prefix,
  artworkLevel,
  setArtworkLevel,
}: {
  filterProducts?: any;
  prefix:
    | ArtworkCollectionPersistentData["prefix"]
    | ArtworkMetaPersistentData["prefix"]
    | ArtworkPrintingPersistentData["prefix"];
  artworkLevel?: ArtworkLevelType;
  setArtworkLevel?: React.Dispatch<React.SetStateAction<ArtworkLevelType>>;
}) => {
  const { state, dispatch } = useMetaArtworkSearchStore();

  const handleToggle = (newState: string) => {
    dispatch(MasterArtworkSearchActions.setArtworkLevel(newState));
  };

  const axios = useAxios();
  const { searchDebounce } = useContext<DebounceContextType>(DebounceContext);
  const authContext = useContext(AuthContext);

  //Collection variables
  const { state: collection, dispatch: dispatchCollection } =
    useMasterArtworkCollectionStore();

  //Meta artwokr
  const { state: stateMetaArtwork, dispatch: dispatchName } =
    useMasterArtworkNumberStore();

  // Artwork
  const { state: statePrinting, dispatch: dispatchPrinting } =
    useMasterArtworkPrintingTechniqueStore();
  //
  const { data: licensors, isLoading: isLicensorsLoading } =
    useLicensorContext();

  const { data: licensedProperties, isLoading: isLicensedPropertiesLoading } =
    useLicensedProperty({
      licensorId: String(collection.licensor?.id?.toString() ?? ""),
    });

  const licensedPropertiesOptions = async (search: string, callback: any) => {
    if (Predicates.isNullOrUndefined(search) || search.length < 3)
      return getLicensedPropertys(licensedProperties);
    const response = await fetchLicensedProperty({
      licensorId: String(String(collection.licensor?.id?.toString() ?? "")),
      search,
      axios,
    });
    callback(getLicensedPropertys(response));
  };

  const { data: globalProducts, isLoading: isGlobalProductsLoading } =
    useGlobalProduct({
      category: undefined,
      sub_category: undefined,
      product_line: undefined,
    });

  const { data: moldsFetched, isLoading: isMoldsLoading } = useMoldContext();

  const loadMolds = async (input: string, callback: any) => {
    let options: any = getMolds(moldsFetched);
    if (input.length > 2) {
      const response = await fetchMold({ search: input, axios });
      options = getMolds(response);
    }
    callback(options);
  };

  const { data: decorationTechs, isLoading: isDecoTechsLoading } =
    useDecorationTechContext();

  const { data: colors, isLoading: isColorsLoading } = useColorContext();

  const loadColorOptions = async (search: string, callback: any) => {
    if (Predicates.isNullOrUndefined(search) || search.length < 3) return [];
    const response = await fetchColor({
      search,
      axios,
    });

    callback(getColors(response));
  };

  //FETCH COLLECTION, META ARTWORK AND Artwork
  const { data: collections, isLoading: collectionsIsLoading } =
    useArtworkCollection({});

  const { data: artworkNameData, isLoading: artworkNameIsLoading } =
    useArtworkName({});
  const artworkNameOptions = getArtworkName(artworkNameData);

  const { data: artworkNumberData, isLoading: artworkNumberIsLoading } =
    useArtworkNumber({});
  const artworkNumberOptions = getArtworkNumber(artworkNumberData);

  const { data: printingTechnique, isLoading: printingTechniqueIsLoading } =
    useArtworkPrintingTechnique({});
  const printingTechniqueOptions = getPrintingTechniques(printingTechnique);

  const buttonDisabled = useMemo(() => {
    if (state.artworkLevel === "collection") {
      return isCollectionStateInitial(collection);
    }
    if (state.artworkLevel === "meta-artwork") {
      return isArtworkNumberStateInitial(stateMetaArtwork);
    }
    if (state.artworkLevel === "printing-technique") {
      return isPrintingTechniqueStateInitial(statePrinting);
    }
    return true;
  }, [artworkLevel, state, collection, stateMetaArtwork, statePrinting]);

  const collectionsOptions = getCollections(collections);
  // const printingTechniquesOptions = getPrintingTechniques(collections);

  const handleSubmit = (e: any) => {
    e.preventDefault();
    const dropdownFilters = {
      artworkName: collection.name,
      collection: collection.code,
      globalProduct: collection.global_product,
      licensor: collection.licensor,
      licensedProperty: collection.licensed_property,
    };

    const filterObject =
      state.artworkLevel === "collection"
        ? buildArtworkCollectionSearchFilterObject(collection)
        : state.artworkLevel === "meta-artwork"
          ? buildMetaArtworkSearchFilterObject(stateMetaArtwork)
          : buildArtworkPrintingTechniqueSearchFilterObject(statePrinting);

    filterProducts(filterObject, dropdownFilters, prefix);
  };

  const clearAllFilters = () => {
    if (state.artworkLevel === "collection") {
      dispatchCollection(MasterArtworkCollectionActions.resetState());
    } else if (state.artworkLevel === "meta-artwork") {
      dispatchName(MasterArtworkNumberActions.resetAllData());
    } else if (state.artworkLevel === "printing-technique") {
      dispatchPrinting(MasterArtworkPrintingTechniqueActions.resetState());
    }
    storeLastFiltersAndResults({}, {}, prefix);
  };

  const searchToObject = (searchString: any) => {
    return Object.fromEntries(new URLSearchParams(searchString));
  };

  const printingTechniqueSearchObject = searchToObject(
    state.appliedFiltersPrintingTechnique,
  );

  return (
    <form className="gx-3 gy-2" onSubmit={handleSubmit}>
      <div
        className="container-fluid mt-3 bg-white shadow py-3 rounded-1 d-flex flex-column justify-contnet-between h-100"
        style={{ minHeight: "30vh" }}
      >
        <div className="mb-1">
          <fieldset className="form-group d-flex gap-2">
            <p className="mr-2">Search for:</p>

            <div className="form-check">
              <input
                type="radio"
                id="collection"
                name="artwork"
                className="form-check-input"
                value="collection"
                defaultChecked={true}
                onChange={(e: any) => {
                  handleToggle("collection");
                }}
              />
              <label
                className="form-check form-check-inline"
                htmlFor="collection"
              >
                Collection
              </label>
            </div>
            <div className="form-check">
              <input
                type="radio"
                id="meta-artwork"
                name="artwork"
                className="form-check-input"
                value="metat-artwork"
                onChange={(e: any) => {
                  handleToggle("meta-artwork");
                }}
              />
              <label
                className="form-check form-check-inline"
                htmlFor="meta-artwork"
              >
                Meta artwork
              </label>
            </div>
            <div className="form-check">
              <input
                type="radio"
                id="printing-technique"
                name="artwork"
                className="form-check-input"
                value="printing-technique"
                onChange={(e: any) => {
                  handleToggle("printing-technique");
                }}
              />
              <label
                className="form-check form-check-inline"
                htmlFor="printing-technique"
              >
                Artwork
              </label>
            </div>
          </fieldset>
        </div>

        {/*COLLECTIONS*/}
        {state.artworkLevel === "collection" && (
          <ExpandableSection
            sectionId={"collection"}
            title="Search Collection"
            className="mt-1"
            expandedByDefault={true}
          >
            <div className="row d-flex mb-0">
              <div className="col-md-3 mb-2 mt-1">
                <label className="form-label">Collection Code/Name*</label>
                <AsyncSelect
                  name="collection"
                  value={
                    Predicates.isNotNullAndNotUndefined(collection.code)
                      ? formatCollectionOptions(collection)
                      : null
                  }
                  onChange={(e) =>
                    dispatchCollection(
                      MasterArtworkCollectionActions.setCollection(
                        // @ts-ignore
                        e?.value ?? null,
                      ),
                    )
                  }
                  defaultOptions={collectionsOptions}
                  loadOptions={(input, callback) => {
                    searchDebounce(
                      collectionsOptions,
                      input,
                      getCollections(collections),
                      callback,
                    );
                  }}
                  formatOptionLabel={OptionFormatter}
                  isLoading={collectionsIsLoading}
                  isClearable
                  isSearchable
                  placeholder="(min 3 characters)"
                  classNamePrefix="react-select"
                />
              </div>

              <div className="col mb-2 mt-1">
                <label className="form-label">Collection Licensor</label>
                <Select
                  onChange={(e) =>
                    dispatchCollection(
                      MasterArtworkCollectionActions.setLicensor(
                        e?.value ?? null,
                      ),
                    )
                  }
                  value={formatLicensorIntoOption(collection.licensor)}
                  formatOptionLabel={OptionFormatter}
                  options={getLicensors(licensors)}
                  isClearable={true}
                  classNamePrefix="react-select"
                  isLoading={isLicensorsLoading}
                />
              </div>

              <div className="col mb-2 mt-1">
                <label className="form-label">Collection License</label>
                <AsyncSelect
                  loadOptions={(input, callback) => {
                    searchDebounce(
                      licensedPropertiesOptions,
                      input,
                      getLicensedPropertys(licensedProperties),
                      callback,
                    );
                  }}
                  defaultOptions={getLicensedPropertys(licensedProperties)}
                  onChange={(e) =>
                    dispatchCollection(
                      MasterArtworkCollectionActions.setLicensedProperty(
                        e?.value ?? null,
                      ),
                    )
                  }
                  formatOptionLabel={OptionFormatter}
                  value={formatLicensedPropertyIntoOption(
                    collection.licensed_property,
                  )}
                  classNamePrefix="react-select"
                  placeholder=""
                  isClearable
                  isLoading={isLicensedPropertiesLoading}
                  components={{
                    IndicatorSeparator: () => null,
                  }}
                />
              </div>

              <div className="col mb-2 mt-1">
                <label className="form-label">Global Product Name-Code</label>
                <AsyncSelect
                  name="globalProduct"
                  value={getGlobalProducts(collection.global_product ?? [])}
                  onChange={(e) =>
                    dispatchCollection(
                      MasterArtworkCollectionActions.setGlobalProduct(
                        e.map(
                          (globalProductOption: any) =>
                            globalProductOption.value,
                        ),
                      ),
                    )
                  }
                  defaultOptions={getGlobalProduct(globalProducts, {})}
                  loadOptions={(input, callback) => {
                    searchDebounce(
                      globalProductOptions,
                      input,
                      getGlobalProduct(globalProducts, {}),
                      callback,
                    );
                  }}
                  isMulti
                  formatOptionLabel={OptionFormatter}
                  // isLoading={isGlobalProductsLoading}
                  isClearable
                  isSearchable
                  placeholder="(min 3 characters)"
                  classNamePrefix="react-select"
                  isDisabled={collection.isReadOnly}
                />
              </div>

              {/*<div className="col mb-2 mt-1">*/}
              {/*  <label className="form-label">Collection Launch Date</label>*/}
              {/*  <input*/}
              {/*    onChange={(e) =>*/}
              {/*      dispatchCollection(*/}
              {/*        MasterArtworkCollectionActions.setLaunchDate(*/}
              {/*          e.target.value,*/}
              {/*        ),*/}
              {/*      )*/}
              {/*    }*/}
              {/*    value={collection.launch_date}*/}
              {/*    type="date"*/}
              {/*    className="form-control d-inline"*/}
              {/*  />*/}
              {/*</div>*/}
            </div>
          </ExpandableSection>
        )}

        {/*Meta artwork*/}
        {state.artworkLevel === "meta-artwork" && (
          <ExpandableSection
            sectionId={"artwork"}
            title="Search Meta Artwork"
            className="mt-1"
            expandedByDefault={true}
          >
            <div className="row d-flex mb-0">
              <div className="col-md-3 mb-2 mt-1">
                <label className="form-label">Meta Artwork Code/Name*</label>
                <AsyncSelect
                  name="artwork-name-code"
                  value={
                    Predicates.isNotNullAndNotUndefined(
                      stateMetaArtwork.artwork_name,
                    )
                      ? formatArtworkNameOptions(stateMetaArtwork.artwork_name)
                      : null
                  }
                  onChange={(e) => {
                    dispatchName(
                      MasterArtworkNumberActions.setArtworkName(
                        e?.value ?? null,
                      ),
                    );
                    dispatchName(
                      MasterArtworkNumberActions.setCollection(
                        e?.value.collection ?? null,
                      ),
                    );
                  }}
                  defaultOptions={artworkNameOptions}
                  loadOptions={(input, callback) => {
                    searchDebounce(
                      artworkNameOptions,
                      input,
                      getArtworkName(artworkNameData),
                      callback,
                    );
                  }}
                  formatOptionLabel={OptionFormatter}
                  isLoading={artworkNameIsLoading}
                  isClearable
                  isSearchable
                  placeholder="(min 3 characters)"
                  classNamePrefix="react-select"
                  // isDisabled={state.isReadOnly}
                />
              </div>

              <div className="col mb-2 mt-1">
                <label className="form-label">Meta Artwork Number</label>
                <AsyncSelect
                  name="artworkName"
                  value={
                    Predicates.isNotNullAndNotUndefined(stateMetaArtwork.code)
                      ? formatArtworkNumberOptions(stateMetaArtwork)
                      : null
                  }
                  onChange={(e: any) => {
                    dispatchName(
                      MasterArtworkNumberActions.setArtworkNumber(
                        e?.value ?? null,
                      ),
                    );
                    dispatchName(
                      MasterArtworkNumberActions.setArtworkName(
                        e?.value.artwork_name ?? null,
                      ),
                    );
                    dispatchName(
                      MasterArtworkNumberActions.setCollection(
                        e?.value.collection ?? null,
                      ),
                    );
                  }}
                  defaultOptions={artworkNumberOptions}
                  loadOptions={(input, callback) => {
                    searchDebounce(
                      artworkNumberOptions,
                      input,
                      getArtworkNumber(artworkNumberData),
                      callback,
                    );
                  }}
                  formatOptionLabel={OptionFormatter}
                  isLoading={artworkNumberIsLoading}
                  isClearable
                  isSearchable
                  placeholder="(min 3 characters)"
                  classNamePrefix="react-select"
                  // isDisabled={state.isReadOnly}
                />
              </div>
              <div className="col mb-2 mt-1">
                <label className="form-label">Collection Code/Name</label>
                <AsyncSelect
                  name="collection"
                  value={
                    Predicates.isNotNullAndNotUndefined(
                      stateMetaArtwork.collection,
                    )
                      ? formatCollectionOptions(stateMetaArtwork.collection)
                      : null
                  }
                  onChange={(e) =>
                    dispatchName(
                      MasterArtworkNumberActions.setCollection(
                        e?.value ?? null,
                      ),
                    )
                  }
                  defaultOptions={collectionsOptions}
                  loadOptions={(input, callback) => {
                    searchDebounce(
                      collectionsOptions,
                      input,
                      getCollections(collections),
                      callback,
                    );
                  }}
                  formatOptionLabel={OptionFormatter}
                  isLoading={collectionsIsLoading}
                  isClearable
                  isSearchable
                  placeholder="(min 3 characters)"
                  classNamePrefix="react-select"
                  // isDisabled={state.isReadOnly}
                />
              </div>

              {/*<div className="col mb-2 mt-1">*/}
              {/*  <label className="form-label">Meta Artwork Launch Date</label>*/}
              {/*  <input*/}
              {/*    onChange={(e) =>*/}
              {/*      dispatchName(*/}
              {/*         MasterArtworkNumberActions.setLaunchDate(*/}
              {/*           e.target.value,*/}
              {/*         ),*/}
              {/*       )*/}
              {/*     }*/}
              {/*     value={stateMetaArtwork.launch_date}*/}
              {/*    type="date"*/}
              {/*    className="form-control d-inline"*/}
              {/*  />*/}
              {/*</div>*/}
            </div>
          </ExpandableSection>
        )}

        {/*Artwork*/}
        {state.artworkLevel === "artwork" && (
          <ExpandableSection
            sectionId={"printing"}
            title="Search Artwork"
            className="mt-1"
            expandedByDefault={true}
          >
            <div className="row d-flex mb-0">
              <div className="col-md-1 mb-2 mt-1">
                <label className="form-label">Code</label>
                <input
                  className="form-control"
                  id="id"
                  name="id"
                  type="text"
                  placeholder="(min-3 characters)"
                  onChange={(e) => {
                    const updatedValue = e.target.value;
                    const code = updatedValue.split("-");

                    const searchableCode = code.length > 1 ? code[1] : code[0];

                    if (searchableCode.length > 2) {
                      dispatchPrinting(
                        MasterArtworkPrintingTechniqueActions.setId(
                          searchableCode.replace(/^0+/, ""),
                        ),
                      );
                    }
                  }}
                  defaultValue={printingTechniqueSearchObject?.id}
                />
                {/*<AsyncSelect*/}
                {/*  name="printing-tecnique"*/}
                {/*  onChange={(e) =>*/}
                {/*    dispatchPrinting(*/}
                {/*      MasterArtworkPrintingTechniqueActions.setPrintingTechnique(*/}
                {/*        e?.value ?? null,*/}
                {/*      ),*/}
                {/*    )*/}
                {/*  }*/}
                {/*  defaultOptions={printingTechniqueOptions}*/}
                {/*  loadOptions={(input, callback) => {*/}
                {/*    searchDebounce(*/}
                {/*      printingTechniqueOptions,*/}
                {/*      input,*/}
                {/*      getPrintingTechniques(printingTechnique),*/}
                {/*      callback,*/}
                {/*    );*/}
                {/*  }}*/}
                {/*  formatOptionLabel={OptionFormatter}*/}
                {/*  isLoading={printingTechniqueIsLoading}*/}
                {/*  isClearable*/}
                {/*  isSearchable*/}
                {/*  placeholder="(min 3 characters)"*/}
                {/*  classNamePrefix="react-select"*/}
                {/*/>*/}
              </div>

              <div className="col-md-3 mb-2 mt-1">
                <label className="form-label">Name or Description</label>
                <input
                  className="form-control"
                  id="pt_description"
                  name="printing-tecnique"
                  type="text"
                  placeholder="(min-3 characters)"
                  onChange={(e) =>
                    dispatchPrinting(
                      MasterArtworkPrintingTechniqueActions.setName(
                        e.target.value || "",
                      ),
                    )
                  }
                  defaultValue={printingTechniqueSearchObject?.name}
                />
              </div>

              <div className="col mb-2 mt-1">
                <label className="form-label">Molds</label>
                <AsyncSelect
                  cacheOptions
                  loadOptions={(input, callback) => {
                    searchDebounce(
                      loadMolds,
                      input,
                      getMolds(moldsFetched) ?? [],
                      callback,
                    );
                  }}
                  defaultOptions={getMolds(moldsFetched)}
                  onChange={(e) =>
                    dispatchPrinting(
                      MasterArtworkPrintingTechniqueActions.setMolds(
                        e.map((moldOption) => moldOption.value),
                      ),
                    )
                  }
                  value={getMolds(statePrinting.molds ?? [])}
                  formatOptionLabel={OptionFormatter}
                  isOptionDisabled={() =>
                    Predicates.isNotNullAndNotUndefined(statePrinting.molds) &&
                    statePrinting.molds.length >= 5
                  }
                  isMulti
                  classNamePrefix="react-select"
                  placeholder="(min 3 characters, up to 5 molded components)"
                  isClearable
                  isLoading={isMoldsLoading}
                  components={{
                    IndicatorSeparator: () => null,
                  }}
                  isDisabled={statePrinting.isReadOnly}
                />
              </div>
              <div className="col mb-2 mt-1">
                <label className="form-label">Decoration Technique</label>
                <Select
                  options={getDecorTechs(decorationTechs.slice(1,decorationTechs.length))}
                  onChange={(e: any) =>
                    dispatchPrinting(
                      MasterArtworkPrintingTechniqueActions.setDecorationTechnique(
                        e.value,
                      ),
                    )
                  }
                  value={
                    Predicates.isNotNullAndNotUndefined(
                      statePrinting.decoration_technique,
                    )
                      ? formatDecorTechIntoOption(
                          statePrinting.decoration_technique,
                        )
                      : null
                  }
                  placeholder=""
                  formatOptionLabel={OptionFormatter}
                  classNamePrefix="react-select"
                  isLoading={isDecoTechsLoading}
                  isClearable
                  components={{
                    IndicatorSeparator: () => null,
                  }}
                  isDisabled={statePrinting.isReadOnly}
                />
              </div>
              <div className="col mb-2 mt-1">
                <label className="form-label">Artwork Color</label>
                <AsyncSelect
                  cacheOptions
                  loadOptions={(input, callback) => {
                    searchDebounce(
                      loadColorOptions,
                      input,
                      getColors(colors),
                      callback,
                    );
                  }}
                  defaultOptions={getColors(colors)}
                  onChange={(e: any) => {
                    dispatchPrinting(
                      MasterArtworkPrintingTechniqueActions.setColor(e.value),
                    );
                  }}
                  value={
                    Predicates.isNotNullAndNotUndefined(statePrinting.color)
                      ? formatColorIntoOption(statePrinting.color)
                      : null
                  }
                  placeholder="(min 3 characters)"
                  formatOptionLabel={OptionFormatter}
                  classNamePrefix="react-select"
                  isClearable
                  isLoading={isColorsLoading}
                  components={{
                    IndicatorSeparator: () => null,
                  }}
                  isDisabled={statePrinting.isReadOnly}
                />
              </div>
            </div>
            <div className="row d-flex mb-0">
              <div className="col mb-2 mt-1">
                <label className="form-label">Meta Artwork Code/Name</label>
                <AsyncSelect
                  name="artwork-name-code"
                  value={
                    Predicates.isNotNullAndNotUndefined(
                      statePrinting.artwork_name,
                    )
                      ? formatArtworkNameOptions(statePrinting.artwork_name)
                      : null
                  }
                  onChange={(e) => {
                    dispatchPrinting(
                      MasterArtworkPrintingTechniqueActions.setArtworkName(
                        e?.value ?? null,
                      ),
                    );
                    dispatchPrinting(
                      MasterArtworkPrintingTechniqueActions.setCollection(
                        e?.value.collection ?? null,
                      ),
                    );
                  }}
                  defaultOptions={artworkNameOptions}
                  loadOptions={(input, callback) => {
                    searchDebounce(
                      artworkNameOptions,
                      input,
                      getArtworkName(artworkNameData),
                      callback,
                    );
                  }}
                  formatOptionLabel={OptionFormatter}
                  isLoading={artworkNameIsLoading}
                  isClearable
                  isSearchable
                  placeholder="(min 3 characters)"
                  classNamePrefix="react-select"
                  isDisabled={statePrinting.isReadOnly}
                />
              </div>

              <div className="col mb-2 mt-1">
                <label className="form-label">Meta Artwork Number</label>
                <AsyncSelect
                  name="artworkName"
                  value={
                    Predicates.isNotNullAndNotUndefined(
                      statePrinting.artwork_number,
                    )
                      ? formatArtworkNumberOptions(statePrinting.artwork_number)
                      : null
                  }
                  onChange={(e: any) => {
                    dispatchPrinting(
                      MasterArtworkPrintingTechniqueActions.setArtworkNumber(
                        e?.value ?? null,
                      ),
                    );
                    dispatchPrinting(
                      MasterArtworkPrintingTechniqueActions.setArtworkName(
                        e?.value.artwork_name ?? null,
                      ),
                    );
                    dispatchPrinting(
                      MasterArtworkPrintingTechniqueActions.setCollection(
                        e?.value.collection ?? null,
                      ),
                    );
                  }}
                  defaultOptions={artworkNumberOptions}
                  loadOptions={(input, callback) => {
                    searchDebounce(
                      artworkNumberOptions,
                      input,
                      getArtworkNumber(artworkNumberData),
                      callback,
                    );
                  }}
                  formatOptionLabel={OptionFormatter}
                  isLoading={artworkNumberIsLoading}
                  isClearable
                  isSearchable
                  placeholder="(min 3 characters)"
                  classNamePrefix="react-select"
                  isDisabled={statePrinting.isReadOnly}
                />
              </div>
              <div className="col mb-2 mt-1">
                <label className="form-label">Collection Code/Name</label>
                <AsyncSelect
                  name="collection"
                  value={
                    Predicates.isNotNullAndNotUndefined(
                      statePrinting.collection,
                    )
                      ? formatCollectionOptions(statePrinting.collection)
                      : null
                  }
                  onChange={(e) =>
                    dispatchPrinting(
                      MasterArtworkPrintingTechniqueActions.setCollection(
                        e?.value ?? null,
                      ),
                    )
                  }
                  defaultOptions={collectionsOptions}
                  loadOptions={(input, callback) => {
                    searchDebounce(
                      collectionsOptions,
                      input,
                      getCollections(collections),
                      callback,
                    );
                  }}
                  formatOptionLabel={OptionFormatter}
                  isLoading={collectionsIsLoading}
                  isClearable
                  isSearchable
                  placeholder="(min 3 characters)"
                  classNamePrefix="react-select"
                  // isDisabled={state.isReadOnly}
                />
              </div>

              <div className="col mb-2 mt-1">
                <label className="form-label">Meta Artwork Launch Date</label>
                <textarea
                  rows={1}
                  className="form-control d-inline no-resize horizontal-scroll"
                  value={statePrinting.artwork_name?.launch_date?.split(" ")[0]}
                  key={statePrinting.artwork_name?.launch_date?.split(" ")[0]}
                  disabled
                />
              </div>
            </div>
          </ExpandableSection>
        )}

        <div className="d-flex justify-content-end mt-3 gap-3">
          <input
            disabled={buttonDisabled}
            type="button"
            onClick={clearAllFilters}
            className="btn btn-outline-primary"
            value="Clear Filters"
          />
          <input
            disabled={buttonDisabled}
            type="submit"
            className="btn btn-primary"
            value="Search"
          />
        </div>
      </div>
    </form>
  );
};
export default SearchFilters;
